import { clientUpdateDownloadCount } from '@/client'
import {
	BulkDownloadSelection,
	DownloadTrackStems,
	mapTracksToBulkStemItems,
} from '@/client/downloads'
import { trackHasStems } from '@/utils/trackHasStems'

import {
	BulkDownloadSelectionAnalytics,
	DownloadFormValues,
	DownloadOption,
	MixesSelectionAnalytics,
} from '../DownloadOptionsModal.types'
import {
	ADD_ALL_MIXES_FIELD,
	ADD_ALL_STEMS_FIELD,
	MAX_DOWNLOAD_FILES,
	UNLABELLED_MIX_OPTION_KEY,
} from './DownloadOptions.constants'

const MAIN_MIX_VARIATION_LABEL = 'main'

type MixFieldKey = 'mixType' | 'mixVariation'

export type FileFormat = 'wav' | 'aiff' | 'mp3'

export const getOptionName = (option: DownloadOption): string => {
	return `${option.category}-${option.label}`
}

export const getFileFormatOptionValue = (
	modalId: string,
	format: FileFormat
): string => `${modalId}-${format}`

export const getFileFormatFieldName = (modalId: string): string =>
	`${modalId}-fileFormat`

export const getFileTypeFromOptionValue = (
	modalId: string,
	optionValue: string
): FileFormat => {
	const fileType = optionValue.replace(`${modalId}-`, '')

	if (fileType === 'aiff' || fileType === 'mp3') {
		return fileType
	}

	return 'wav'
}

export const getUniqueTracks = (tracks: Track[]): Track[] =>
	Array.from(new Map(tracks.map((track) => [track.id, track])).values())

export const getMainTrackId = (track: Track): number => {
	if (track.mix !== 1 && track.parentId) {
		return track.parentId
	}

	return track.id
}

export const getMixFieldValue = (
	track: Track,
	key: MixFieldKey
): string | null => {
	const value = track[key]

	if (value == null || value === '') {
		return null
	}

	return value
}

export const isCutdownTrack = (track: Track): boolean =>
	Boolean(getMixFieldValue(track, 'mixType'))

export const isUnlabelledMixTrack = (track: Track): boolean =>
	!getMixFieldValue(track, 'mixType') &&
	!getMixFieldValue(track, 'mixVariation')

export const getFullLengthMixTracks = (tracks: Track[]): Track[] =>
	tracks.filter((track) => !isCutdownTrack(track))

export const hasStemsOnTracks = (tracks: Track[]): boolean =>
	tracks.some(trackHasStems)

export const getPluralMessageKey = <
	TSingular extends string,
	TPlural extends string
>(
	singular: TSingular,
	plural: TPlural,
	count: number
): TSingular | TPlural => (count === 1 ? singular : plural)

export const getNoMixesMessageKey = (
	trackCount: number
): 'has-no-mixes' | 'has-no-mixes-multiple' =>
	getPluralMessageKey('has-no-mixes', 'has-no-mixes-multiple', trackCount)

export const getNoStemsMessageKey = (
	trackCount: number
): 'has-no-stems' | 'has-no-stems-multiple' =>
	getPluralMessageKey('has-no-stems', 'has-no-stems-multiple', trackCount)

export const hasMixOptionsOnTracks = (tracks: Track[]): boolean =>
	tracks.some((track) => track.mixCount !== 1)

export const hasMixOptionsInDownloadResponse = (mixes: Track[]): boolean =>
	getOptionCounts(mixes, 'mixType', 'mixTypes').length > 0 ||
	getOptionCounts(mixes, 'mixVariation', 'mixVariations').length > 0

export const hasUnlabelledMixTracks = (mixes: Track[]): boolean =>
	mixes.some(isUnlabelledMixTrack)

const getUnlabelledMixOptionName = (): string =>
	getOptionName({
		category: 'mixTypes',
		count: 0,
		label: UNLABELLED_MIX_OPTION_KEY,
	})

export const shouldIncludeUnlabelledMixes = (
	formValues: DownloadFormValues
): boolean =>
	Boolean(formValues[ADD_ALL_MIXES_FIELD]) ||
	Boolean(formValues[getUnlabelledMixOptionName()])

const getUnlabelledMixOption = (mixes: Track[]): DownloadOption | null => {
	const hasLabelledMixOptions = hasMixOptionsInDownloadResponse(mixes)
	const unlabelledMixCount = mixes.filter(isUnlabelledMixTrack).length

	if (!hasLabelledMixOptions || unlabelledMixCount === 0) {
		return null
	}

	return {
		category: 'mixTypes',
		count: unlabelledMixCount,
		label: UNLABELLED_MIX_OPTION_KEY,
	}
}

export const getMixDownloadOptions = (
	mixes: Track[],
	selectedTracks: Track[] = []
): DownloadOption[] => {
	const labelledOptions = [
		...getOptionCounts(mixes, 'mixType', 'mixTypes'),
		...getOptionCounts(
			getFullLengthMixesExcludingSelectedMains(mixes, selectedTracks),
			'mixVariation',
			'mixVariations'
		),
	]
	const unlabelledMixOption = getUnlabelledMixOption(mixes)

	if (!unlabelledMixOption) {
		return labelledOptions
	}

	return [...labelledOptions, unlabelledMixOption]
}

// Albums can report a bad mixCount; fall back to fetched mix options, including
// mixes that have neither mixType nor mixVariation.
export const shouldShowMixOptionsSection = (
	initialTracks: Track[],
	mixes: Track[] | undefined,
	isLoading: boolean
): boolean => {
	if (!hasMixOptionsOnTracks(initialTracks)) {
		return false
	}

	if (isLoading) {
		return true
	}

	const candidateMixes = mixes ?? []

	return (
		hasMixOptionsInDownloadResponse(candidateMixes) ||
		hasUnlabelledMixTracks(candidateMixes)
	)
}

const isMainMixVariationLabel = (label: string): boolean =>
	label.toLowerCase() === MAIN_MIX_VARIATION_LABEL

const isFullLengthMainMix = (track: Track): boolean => {
	const mixVariation = getMixFieldValue(track, 'mixVariation')

	return (
		mixVariation !== null &&
		isMainMixVariationLabel(mixVariation) &&
		!isCutdownTrack(track)
	)
}

const getFullLengthMixesExcludingSelectedMains = (
	mixes: Track[],
	selectedTracks: Track[]
): Track[] => {
	const selectedTrackIds = new Set(selectedTracks.map((track) => track.id))

	return getFullLengthMixTracks(mixes).filter(
		(track) => !isFullLengthMainMix(track) || !selectedTrackIds.has(track.id)
	)
}

const sortDownloadOptionLabels = (
	current: DownloadOption,
	next: DownloadOption,
	key: MixFieldKey
): number => {
	if (key === 'mixVariation') {
		const currentIsMain = isMainMixVariationLabel(current.label)
		const nextIsMain = isMainMixVariationLabel(next.label)

		if (currentIsMain && !nextIsMain) {
			return -1
		}

		if (!currentIsMain && nextIsMain) {
			return 1
		}
	}

	return current.label.localeCompare(next.label)
}

export const getOptionCounts = (
	tracks: Track[],
	key: MixFieldKey,
	category: DownloadOption['category']
): DownloadOption[] => {
	const counts = tracks.reduce<Record<string, number>>((result, track) => {
		const label = getMixFieldValue(track, key)

		if (!label) {
			return result
		}

		return {
			...result,
			[label]: (result[label] ?? 0) + 1,
		}
	}, {})

	return Object.entries(counts)
		.map(([label, count]) => ({ category, count, label }))
		.sort((current, next) => sortDownloadOptionLabels(current, next, key))
}

export const isSelectedMixVariationTrack = (
	track: Track,
	formValues: DownloadFormValues
): boolean => {
	if (isCutdownTrack(track)) {
		return false
	}

	const mixVariation = getMixFieldValue(track, 'mixVariation')

	return mixVariation
		? Boolean(formValues[`mixVariations-${mixVariation}`])
		: false
}

export const isSelectedCutdownTrack = (
	track: Track,
	formValues: DownloadFormValues
): boolean => {
	const mixType = getMixFieldValue(track, 'mixType')

	return mixType ? Boolean(formValues[`mixTypes-${mixType}`]) : false
}

export const mergeStemsOntoTracks = (
	tracks: Track[],
	stems: DownloadTrackStems[] = []
): Track[] => {
	if (stems.length === 0) {
		return tracks
	}

	const stemsByTrackId = new Map(
		stems.map((entry) => [entry.id, entry.stems ?? []])
	)

	return tracks.map((track) => {
		const trackStems = stemsByTrackId.get(track.id)

		if (!trackStems) {
			return track
		}

		return {
			...track,
			stems: trackStems,
		}
	})
}

const countSelectedStems = (selection: BulkDownloadSelection): number =>
	selection.stems.length

const parentStemLookupKey = (
	albumId: number,
	albumTrackNumber: number,
	trackTitle: string
): string => `${albumId}:${albumTrackNumber}:${trackTitle}`

export type StemDownloadCountUpdate = {
	track: Track
	stemCount: number
}

export const getStemDownloadCountUpdates = (
	selection: BulkDownloadSelection,
	stemsDownloadTrack?: Track
): StemDownloadCountUpdate[] => {
	if (selection.stems.length === 0) {
		return []
	}

	const stemCountByParentKey = new Map<string, number>()

	for (const stem of selection.stems) {
		const key = parentStemLookupKey(
			stem.albumId,
			stem.albumTrackNumber,
			stem.trackTitle
		)
		stemCountByParentKey.set(key, (stemCountByParentKey.get(key) ?? 0) + 1)
	}

	const parentTracks = [
		...selection.tracks,
		...selection.mixes,
		...selection.cutdowns,
	]

	const updates: StemDownloadCountUpdate[] = []

	for (const track of parentTracks) {
		const key = parentStemLookupKey(
			Number(track.album.number),
			Number(track.albumTrackNumber),
			track.title
		)
		const stemCount = stemCountByParentKey.get(key)

		if (stemCount && stemCount > 0) {
			updates.push({ track, stemCount })
		}
	}

	if (updates.length === 0 && stemsDownloadTrack) {
		updates.push({
			track: stemsDownloadTrack,
			stemCount: stemsDownloadTrack.stems?.length ?? selection.stems.length,
		})
	}

	return updates
}

export const getBulkDownloadTracks = (
	selection: BulkDownloadSelection
): Track[] => [...selection.tracks, ...selection.mixes, ...selection.cutdowns]

export const recordBulkDownloadCounts = async (
	selection: BulkDownloadSelection,
	fileType: string,
	userId: number,
	organisationId: number,
	stemsDownloadTrack?: Track
): Promise<void> => {
	const downloadedTracks = getBulkDownloadTracks(selection)

	if (downloadedTracks.length > 0) {
		await clientUpdateDownloadCount(
			downloadedTracks,
			fileType,
			userId,
			organisationId
		)
	}

	for (const { track, stemCount } of getStemDownloadCountUpdates(
		selection,
		stemsDownloadTrack
	)) {
		await clientUpdateDownloadCount(
			track,
			fileType,
			userId,
			organisationId,
			stemCount
		)
	}
}

export const getBulkDownloadSelection = (
	initialTracks: Track[],
	candidateMixes: Track[],
	formValues: DownloadFormValues,
	stemsByTrack: DownloadTrackStems[] = []
): BulkDownloadSelection => {
	const tracks = getUniqueTracks(initialTracks)
	const topLevelTrackIds = new Set(tracks.map((track) => track.id))
	const cutdowns = getUniqueTracks(
		candidateMixes.filter(
			(track) =>
				!topLevelTrackIds.has(track.id) &&
				(isSelectedCutdownTrack(track, formValues) ||
					// Unlabelled mixes are offered under cutdowns as "Other".
					(isUnlabelledMixTrack(track) &&
						shouldIncludeUnlabelledMixes(formValues)))
		)
	)
	const mixes = getUniqueTracks(
		candidateMixes.filter(
			(track) =>
				!topLevelTrackIds.has(track.id) &&
				isSelectedMixVariationTrack(track, formValues)
		)
	)
	const selectedTracks = [...tracks, ...mixes, ...cutdowns]
	const stems = hasSelectedStems(formValues)
		? mapTracksToBulkStemItems(
				mergeStemsOntoTracks(selectedTracks, stemsByTrack),
				getUniqueTracks([...tracks, ...candidateMixes])
		  )
		: []

	return {
		cutdowns,
		mixes,
		stems,
		tracks,
	}
}

export const getAddAllMixFileCount = (
	initialTracks: Track[],
	candidateMixes: Track[]
): number => {
	const formValues: DownloadFormValues = {
		[ADD_ALL_MIXES_FIELD]: true,
	}

	for (const option of getMixDownloadOptions(candidateMixes, initialTracks)) {
		formValues[getOptionName(option)] = true
	}

	const selection = getBulkDownloadSelection(
		initialTracks,
		candidateMixes,
		formValues
	)

	return selection.mixes.length + selection.cutdowns.length
}

export const getStemFileCount = (stems: DownloadTrackStems[] = []): number => {
	const seenStemIds = new Set<number>()

	for (const entry of stems) {
		for (const stem of entry.stems ?? []) {
			seenStemIds.add(stem.id)
		}
	}

	return seenStemIds.size
}

export const getBulkDownloadItemCount = (
	selection: BulkDownloadSelection
): number =>
	selection.tracks.length +
	selection.mixes.length +
	selection.cutdowns.length +
	countSelectedStems(selection)

export const isDownloadFileLimitExceeded = (count: number): boolean =>
	count > MAX_DOWNLOAD_FILES

export const hasSelectedStems = (formValues: DownloadFormValues): boolean =>
	Boolean(formValues[ADD_ALL_STEMS_FIELD])

export const getBulkDownloadSelectionAnalytics = (
	candidateMixes: Track[],
	formValues: DownloadFormValues,
	selectedTracks: Track[] = []
): BulkDownloadSelectionAnalytics => {
	const mixOptionNames = getMixDownloadOptions(
		candidateMixes,
		selectedTracks
	).map(getOptionName)
	const selectedMixOptionCount = mixOptionNames.filter((optionName) =>
		Boolean(formValues[optionName])
	).length
	const unlabelledMixesSelected =
		hasUnlabelledMixTracks(candidateMixes) &&
		shouldIncludeUnlabelledMixes(formValues)

	let mixes: MixesSelectionAnalytics = 'None'

	if (mixOptionNames.length === 0) {
		if (unlabelledMixesSelected) {
			mixes = 'All'
		}
	} else if (selectedMixOptionCount === mixOptionNames.length) {
		mixes = 'All'
	} else if (selectedMixOptionCount > 0) {
		mixes = 'Tailored'
	}

	return {
		Mixes: mixes,
		Stems: hasSelectedStems(formValues) ? 'All' : 'None',
	}
}

export const shouldUseSingleTrackDownload = (
	selection: BulkDownloadSelection,
	formValues: DownloadFormValues
): boolean =>
	selection.tracks.length === 1 &&
	selection.mixes.length === 0 &&
	selection.cutdowns.length === 0 &&
	!hasSelectedStems(formValues)
